import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Shield, FileText, IndianRupee } from 'lucide-react';
import { Button } from './ui/button';
import { fmtINR } from '../lib/utils';
import { PERP_THRESHOLD } from '../lib/constants';

const trustChips = [
  { icon: Shield, label: 'Inflation-adjusted' },
  { icon: IndianRupee, label: 'INR formatted outputs' },
  { icon: FileText, label: 'Downloadable PDF report' },
];

// Animation configs extracted as constants (not recreated per render)
const leftAnim = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, ease: 'easeOut' } };
const rightAnim = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay: 0.15, ease: 'easeOut' } };

// --- Extracted sub-components ---

const TrustChipList = () => (
  <div className="mt-6 flex flex-wrap gap-3">
    {trustChips.map((chip) => (
      <span
        key={chip.label}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-secondary rounded-full border border-border"
      >
        <chip.icon size={14} />
        {chip.label}
      </span>
    ))}
  </div>
);

const PreviewCard = ({ result }) => {
  const renderYears = () => {
    if (!result) return <span className="text-muted-foreground">{String.fromCharCode(8212)}</span>;
    if (result.years_until_target) return `${result.years_until_target} Years`;
    if (result.years_until_depletion) return `${result.years_until_depletion} Years`;
    const sustainYears = result.sustainability_years || PERP_THRESHOLD;
    if (sustainYears >= PERP_THRESHOLD) return `${PERP_THRESHOLD}+ Years`;
    return `${sustainYears} Years`;
  };

  // Determine if wealth depletes or lasts
  const depletes = result?.years_until_depletion !== null && result?.years_until_depletion !== undefined;
  const yearsColor = depletes ? '#dc2626' : '#16a34a'; // Red if depletes, Green if lasts

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-lg">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
        <span className="text-xs text-muted-foreground font-medium">Live Preview</span>
      </div>
      <div className="space-y-4">
        <div>
          <span className="text-xs text-muted-foreground">Sustainability Period</span>
          <div className="font-mono-num text-3xl sm:text-4xl font-bold mt-1" style={{ color: yearsColor }} data-testid="hero-preview-years">
            {renderYears()}
          </div>
        </div>
        <div className="h-px bg-border" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="text-xs text-muted-foreground">Legacy Balance</span>
            <div className="font-mono-num text-sm font-semibold mt-0.5 text-green-600">
              {result ? fmtINR(result.peak_balance) : String.fromCharCode(8212)}
            </div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Total Withdrawn</span>
            <div className="font-mono-num text-sm font-semibold mt-0.5 text-green-600">
              {result ? fmtINR(result.total_withdrawn) : String.fromCharCode(8212)}
            </div>
          </div>
        </div>
        <div className="h-px bg-border" />
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
            12% Annual Return Assumed
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            6% Annual Inflation Adjusted
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main component ---

export const HeroSection = ({ result }) => {
  const scrollToCalculator = useCallback(() => {
    const el = document.querySelector('#calculator');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleConsult = useCallback(() => {
    window.open('tel:+919824050200');
  }, []);

  return (
    <section
      className="relative overflow-hidden"
      style={{
        background:
          'radial-gradient(1200px circle at 20% 10%, hsl(43 74% 92% / 0.6) 0%, transparent 55%), radial-gradient(900px circle at 80% 0%, hsl(226 58% 22% / 0.08) 0%, transparent 60%)',
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 lg:pt-16 pb-12 sm:pb-16">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <motion.div className="lg:col-span-7" {...leftAnim}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary tracking-tight leading-tight">
              How Long Will Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[hsl(226,58%,22%)] to-[hsl(43,74%,52%)]">
                Wealth Last?
              </span>
            </h1>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Estimate how many years your current corpus can sustain your withdrawals {String.fromCharCode(8212)} adjusted
              for inflation. Built for Indian investors planning retirement and long-term cashflows.
            </p>
            <TrustChipList />
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl h-11 px-6 text-base font-semibold active:scale-[0.98]"
                onClick={scrollToCalculator}
                data-testid="hero-start-calculator-button"
                style={{ transition: 'background-color 0.2s ease, box-shadow 0.2s ease' }}
              >
                Start the Calculator
                <ArrowDown size={16} className="ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="rounded-xl h-11 px-6 text-base border-border text-primary hover:bg-secondary"
                onClick={handleConsult}
                data-testid="hero-consult-expert-button"
                style={{ transition: 'background-color 0.2s ease, border-color 0.2s ease' }}
              >
                Consult an Expert
              </Button>
            </div>
          </motion.div>
          <motion.div className="lg:col-span-5" {...rightAnim}>
            <PreviewCard result={result} />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
